import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { MuiPickersUtilsProvider, KeyboardDatePicker } from '@material-ui/pickers';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import Button from '@material-ui/core/Button';
import Draggable from 'react-draggable';
import Paper from '@material-ui/core/Paper';
import Slide from '@material-ui/core/Slide';
import CloseIcon from '@material-ui/icons/Close';
import IconButton from '@material-ui/core/IconButton';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid/index';
import { useSelector } from 'react-redux';
import DateFnsUtils from '@date-io/date-fns';
import moment from 'moment';

/** MUI pickers need a Date or null — not a display string. */
function toPickerDate(val) {
  if (val == null || val === '') return null;
  if (val instanceof Date && !Number.isNaN(val.getTime())) return val;
  if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}/.test(val.trim())) {
    const iso = moment(val);
    if (iso.isValid()) return iso.toDate();
  }
  const strictFormats = ['DD-MM-YY', 'DD/MM/YYYY', 'DD-MM-YYYY', 'YYYY-MM-DD'];
  for (let i = 0; i < strictFormats.length; i++) {
    const m = moment(val, strictFormats[i], true);
    if (m.isValid()) return m.toDate();
  }
  const loose = moment(val);
  return loose.isValid() ? loose.toDate() : null;
}

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
  },
  appBar: {
    position: 'relative',
  },
  title: {
    marginLeft: theme.spacing(2),
    flex: 1,
  },
}));

const PaperComponent = (props) => {
  return (
    <Draggable handle='#draggable-dialog-title' cancel={'[class*="MuiDialogContent-root"]'}>
      <Paper {...props} />
    </Draggable>
  );
};

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction='up' ref={ref} {...props} />;
});

const AddExpenseDialog = ({ openExpense, handleAddExpenses, handleCancleExpenses, isEditing }) => {
  const classes = useStyles();

  const [fields, setFields] = useState([
    { details: null, amount: null, settled: null, date: null },
  ]);

  const { expenses } = useSelector((state) => state.Expenses) || [];

  const [validateExpenses, updateValidateExpenses] = useState(false);
  const handleChange = (i, event) => {
    const values = [...fields];
    values[i].details = event.target.value;
    setFields(values);
  };

  const handleChangeAmount = (i, event) => {
    const values = [...fields];
    values[i].amount = event.target.value;
    setFields(values);
  };

  const handleChangeSettled = (i, event) => {
    const values = [...fields];
    values[i].settled = event.target.value;
    setFields(values);
  };

  const handleChangeDate = (i, date) => {
    const values = [...fields];
    values[i].date = date;
    setFields(values);
  };

  const handleAdd = () => {
    const values = [...fields];
    values.push({ details: null, amount: null, settled: null, date: null });
    setFields(values);
  };

  const handleRemove = (i) => {
    const values = [...fields];
    values.splice(i, 1);
    setFields(values);
  };

  useEffect(() => {
    if (fields.length < 1) {
      updateValidateExpenses(false);
      return;
    }
    const isValid = fields.every(({ details, amount, settled, date }) => {
      const hasDetails = details != null && String(details).trim() !== '';
      const hasAmount = amount !== null && amount !== '';
      const hasSettled = settled !== null && settled !== '';
      const d = toPickerDate(date);
      const hasDate = d != null;
      return hasDetails && hasAmount && hasSettled && hasDate;
    });
    updateValidateExpenses(isValid);
  }, [fields]);

  useEffect(() => {
    if (isEditing && expenses?.expenseDetails?.length) {
      setFields(
        expenses.expenseDetails.map((row) => ({
          ...row,
          date: toPickerDate(row.date),
        }))
      );
    }
  }, [isEditing, expenses]);

  const isValid = fields.length > 0 && validateExpenses;

  return (
    <Dialog
      fullScreen
      open={openExpense}
      onClose={handleCancleExpenses}
      PaperComponent={PaperComponent}
      aria-labelledby='draggable-dialog-title'
      TransitionComponent={Transition}
    >
      <AppBar className={classes.appBar}>
        <Toolbar>
          <IconButton
            edge='start'
            color='inherit'
            onClick={handleCancleExpenses}
            aria-label='close'
          >
            <CloseIcon />
          </IconButton>
          <Typography variant='h6' className={classes.title}>
            Add Expenses
          </Typography>
          <Button
            autoFocus
            color='inherit'
            disabled={!isValid}
            onClick={() => handleAddExpenses(fields)}
          >
            save
          </Button>
        </Toolbar>
      </AppBar>
      <DialogTitle style={{ cursor: 'move' }} id='draggable-dialog-title'>
        Expenses
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          {fields.map((field, idx) => {
            return (
              <div key={`${field}-${idx}`} style={{ marginBottom: '10px' }}>
                <Grid container spacing={3} className={classes.root}>
                  <Grid item xs={3} className={classes.gridItem}>
                    <Box marginRight='10px'>
                      <TextField
                        type='text'
                        placeholder='Enter Expense'
                        label='Expense'
                        variant='outlined'
                        name='expense'
                        value={field.details || ''}
                        onChange={(e) => handleChange(idx, e)}
                        size='small'
                        fullWidth='false'
                        width='30px'
                        required
                        style={{ maxWidth: '80%' }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={2} className={classes.gridItem}>
                    <Box>
                      <TextField
                        type='number'
                        placeholder='Enter Amount'
                        label='Amount'
                        variant='outlined'
                        name='amount'
                        size='small'
                        value={field.amount || ''}
                        onChange={(e) => handleChangeAmount(idx, e)}
                        required
                        style={{ maxWidth: '80%' }}
                      />
                    </Box>
                  </Grid>

                  <Grid item xs={2} className={classes.gridItem}>
                    <Box>
                      <TextField
                        type='number'
                        placeholder='Enter Settled Amount'
                        label='Settled'
                        variant='outlined'
                        name='settled'
                        size='small'
                        value={field.settled || ''}
                        onChange={(e) => handleChangeSettled(idx, e)}
                        required
                        style={{ maxWidth: '80%' }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={3} className={classes.gridItem}>
                    <Box>
                    <Typography lineHeight='10px'>Expense Date</Typography>
                      <MuiPickersUtilsProvider utils={DateFnsUtils}>
                        <KeyboardDatePicker
                          disableToolbar
                          variant='inline'
                          inputVariant='outlined'
                          format='dd/MM/yyyy'
                          margin='normal'
                          id={`expense-date-${idx}`}
                          value={toPickerDate(field.date)}
                          KeyboardButtonProps={{
                            'aria-label': 'change date',
                          }}
                          onChange={(d) => handleChangeDate(idx, d)}
                          style={{ maxWidth: '70%', marginTop: '-10px' }}
                        />
                      </MuiPickersUtilsProvider>
                    </Box>
                  </Grid>
                  <Grid item xs={2} className={classes.gridItem}>
                    <Button type='button' onClick={() => handleRemove(idx)}>
                      REMOVE
                    </Button>
                  </Grid>
                </Grid>
              </div>
            );
          })}

          <Button type='button' onClick={() => handleAdd()} marginBottom='10px'>
            Add More
          </Button>
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button autoFocus onClick={handleCancleExpenses} color='primary'>
          Cancel
        </Button>
        <Button onClick={() => handleAddExpenses(fields)} color='primary' disabled={!isValid}>
          Add Expenses
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddExpenseDialog;
